import React, { useEffect, useLayoutEffect, useMemo, useState } from "react"
import { KSpan, KTitleSpan, KDropdown, KSelectRange, KButton, KTooltip } from "kahuna-base-react-components"
import TableNew from "../../../ui/table/TableNew"
import { getSelectedLanguage, lang } from "../../../../constants/languages"
import getStatementsColumns from "./statementsColumns"
import { RootState } from "../../../../store"
import { connect } from "react-redux"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { summaries, userPayments } from "../../../../apis"
import {
  getLastNMonthsYYYYMM,
  getMonthRange,
  mapListToCamelCase,
  mapToCamelCase,
  yyyymmToDate
} from "../../../../utility"
import { SummaryStatements, UserAdvance } from "../../../../types"
import Loading from "../../../ui/Loading"
import ReceiptModal from "./ReceiptModal"
import { toast } from "react-toastify"
import axios from "axios"

const TypeOptions: KSelectOption[] = [
  { label: lang.royalties.statements.payment, value: 1, value2: "regular" },
  { label: lang.royalties.statements.advance, value: 2, value2: "advance" }
]

const limit = 20

const Statements = ({
  isAdmin,
  selectedUser,
  selectedAccount
}: {
  isAdmin: boolean
  selectedUser?: KSelectOption
  selectedAccount?: KSelectOption
}) => {
  const [loading, setLoading] = useState(false)

  const [offset, setOffset] = useState(0)
  const [summaryList, setSummaryList] = useState<SummaryStatements[]>([])
  const [advanceList, setAdvanceList] = useState<SummaryStatements[]>([])
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [selectedPaymentType, setSelectedPaymentType] = useState<KSelectOption>()
  const [selectedPeriodList, setSelectedPeriodList] = useState<string[]>(getLastNMonthsYYYYMM(12))
  const [hasNext, setHasNext] = useState(false)
  const [receiptModalOpen, setReceiptModalOpen] = useState(false)
  const [receiptData, setReceiptData] = useState<any>(null)
  const [receiptLoading, setReceiptLoading] = useState(false)

  const scrollPosRef = React.useRef(0)

  useLayoutEffect(() => {
    if (!loading && scrollPosRef.current > 0) {
      window.scrollTo({
        top: scrollPosRef.current,
        behavior: "auto"
      })
    }
  }, [summaryList])

  const getRoyaltiesStatements = (
    offset: number,
    selectedPeriodList: string[],
    selectedPaymentType?: KSelectOption
  ) => {
    setLoading(true)
    const isAdminParam = isAdmin ? "&is_admin_page=1" : "&is_admin_page=0"
    const paymentTypeParam = selectedPaymentType ? `&payment_type=${selectedPaymentType.value2}` : ""
    const periodParam =
      selectedPeriodList.length > 0
        ? `&period_start=${selectedPeriodList[0]}&period_end=${selectedPeriodList[selectedPeriodList.length - 1]}`
        : ""
    const userIdParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    const selectedAccountParam = `&selected_account=${selectedAccount?.value2}`

    summaries(
      `/royalties_statements/?limit=${limit}&offset=${offset}${isAdminParam}${paymentTypeParam}${periodParam}${userIdParam}${selectedAccountParam}`,
      "get"
    )
      .then((resp) => {
        setLoading(false)
        if (!resp.data.results) {
          return
        }

        const results: SummaryStatements[] = mapListToCamelCase(resp.data.results)

        if (selectedPeriodList.length === 0 && results.length > 0) {
          setSelectedPeriodList(getLastNMonthsYYYYMM(12))
        }

        if (results.length === limit) {
          setHasNext(true)
        } else {
          setHasNext(false)
        }

        if (offset === 0) {
          setSummaryList(results)
          return
        }

        setSummaryList((prev) => [...prev, ...results])
      })
      .catch(() => setLoading(false))
  }

  const getRoyaltiesAdvances = () => {
    setLoading(true)
    const isAdminParam = isAdmin ? "&is_admin_page=1" : "&is_admin_page=0"
    const userIdParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    const selectedAccountParam = `&selected_account=${selectedAccount?.value2}`

    summaries(`/royalties_advances/?t=1&${isAdminParam}${userIdParam}${selectedAccountParam}`, "get")
      .then((resp) => {
        setLoading(false)
        if (!resp.data.results) {
          return
        }

        const results: SummaryStatements[] = mapListToCamelCase(resp.data.results)
        setAdvanceList((prev) => [...prev, ...results])
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    if (isAdmin && !selectedUser?.value) {
      return
    }
    setSummaryList([])
    setAdvanceList([])
    setSelectedPaymentType(undefined)
    setSelectedPeriodList(getLastNMonthsYYYYMM(12))
    getRoyaltiesStatements(0, [], undefined)
    getRoyaltiesAdvances()
    setOffset(0)
  }, [selectedUser, selectedAccount])

  const handleReceiptClick = async (transactionId: string) => {
    setReceiptLoading(true)
    setReceiptModalOpen(true)
    try {
      const response = await userPayments(`/revolut_transaction_receipt/?transaction_id=${transactionId}`, "get")
      if (response.status === 200) {
        setReceiptData(mapToCamelCase(response.data))
      }
    } catch {
      setReceiptData(null)
    } finally {
      setReceiptLoading(false)
    }
  }

  const handleInvoiceClick = async (id: number, isAdvance: boolean) => {
    try {
      const userIdParam = selectedUser ? `&user_id=${selectedUser.value}` : ""

      const resp = await axios({
        method: "get",
        url: `${process.env.REACT_APP_BASE_URL}/releases/summary/download_statement_invoice/?id=${id}&is_advance=${
          isAdvance ? "1" : "0"
        }&lang=${getSelectedLanguage()}${userIdParam}`,
        responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        withCredentials: true
      })

      const fileName = resp.headers["x-filename"] || "invoice.pdf"

      const blob = new Blob([resp.data], { type: "application/pdf" })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      toast.error(lang.royalties.statements.error.download_failed)
    }
  }

  const columns = useMemo(() => getStatementsColumns(isAdmin, handleReceiptClick, handleInvoiceClick), [])

  const topRightElements = () => {
    const isOpen = selectedPaymentType !== undefined
    return (
      <div className="flex items-center gap-4">
        <div className="cursor-pointer" onClick={() => setFiltersOpen(!filtersOpen)}>
          <KTooltip
            position="left"
            children={<img src={isOpen ? "/reports_icons/filters-on.svg" : "/reports_icons/filters-off.svg"} />}
            content={<KSpan text={lang.catalog.filter_components.filters_tooltip} />}
          />
        </div>
      </div>
    )
  }

  const topFilters = () => {
    if (!filtersOpen) {
      return
    }
    return (
      <div className="flex justify-between p-4" style={{ borderRadius: 10, border: "1px solid #11111120" }}>
        <div className="flex gap-6">
          <div className="flex items-center justify-start max-w-[260px]">
            <KDropdown
              isClearable
              options={TypeOptions}
              onSelect={(element) => setSelectedPaymentType(element as KSelectOption)}
              placeholder={lang.royalties.statements.payment_type}
              showOnlyIconsInMulti
              allowContainerShrink
              selected={selectedPaymentType}
              rightIcon="/analytics_icons/caret-down-new.svg"
              menuWidth={170}
              enableIndicator={true}
              placeholderColor="#000"
              closeMenuOnSelect={false}
            />
          </div>
          {selectedPeriodList.length > 0 && (
            <div className="flex">
              <KSelectRange
                key={`filter-date-range`}
                value={[
                  yyyymmToDate(selectedPeriodList[0]),
                  yyyymmToDate(selectedPeriodList[selectedPeriodList.length - 1])
                ]}
                onChange={(event) => {
                  if (!event) {
                    setSelectedPeriodList([])
                    return
                  }
                  //@ts-ignore
                  setSelectedPeriodList(getMonthRange(event[0], event[1]))
                }}
                height="36px"
                width="auto"
                backgroundColor="#F7F7F7"
                hoverBackgroundColor="#F3F3F3"
                boxShadow="none"
              />
              <div className="flex ml-1 mr-2 items-center gap-1">
                <KSpan text={`${selectedPeriodList[0]}`} />
                {selectedPeriodList.length > 1 && (
                  <KSpan text={`- ${selectedPeriodList[selectedPeriodList.length - 1]}`} />
                )}
              </div>
            </div>
          )}
        </div>
        <div>
          <KButton
            height="36px"
            text={lang.common.apply_filters}
            onClick={() => {
              setOffset(0)
              getRoyaltiesStatements(0, selectedPeriodList, selectedPaymentType)
            }}
            background="black"
            textColor="white"
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex-grow justify-center">
      {loading && <Loading />}
      <div className="w-[100%] flex ">
        <div className="grow min-h-0">
          <TableNew
            className="pt-2.5 -ml-5"
            title={<KTitleSpan fontSize={20} lineHeight="26px" text={lang.royalties.statements.title} />}
            limit={limit}
            columns={columns}
            data={summaryList}
            count={1}
            topRightElement={topRightElements()}
            topFilters={topFilters()}
            notFoundTitle={lang.not_found.reports.title}
            notFoundDesc={lang.not_found.reports.desc}
            tablePaddingTop={6}
          />
        </div>
      </div>
      {hasNext && (
        <div className="flex justify-center pb-6 ">
          <KButton
            width="150px"
            background="transparent"
            onClick={() => {
              scrollPosRef.current = window.scrollY

              setOffset(offset + limit)
              getRoyaltiesStatements(offset + limit, selectedPeriodList, selectedPaymentType)
            }}
            text={lang.royalties.statements.load_more}
          />
        </div>
      )}
      {advanceList.length > 0 && (
        <div className="w-[100%] flex ">
          <div className="grow min-h-0">
            <TableNew
              className="pt-2.5 -ml-5"
              title={<KTitleSpan fontSize={20} lineHeight="26px" text={lang.catalog.advance_payment} />}
              limit={limit}
              columns={columns}
              data={advanceList}
              count={1}
              notFoundTitle={lang.not_found.reports.title}
              notFoundDesc={lang.not_found.reports.desc}
              tablePaddingTop={6}
            />
          </div>
        </div>
      )}

      <ReceiptModal
        open={receiptModalOpen}
        onClose={() => {
          setReceiptModalOpen(false)
          setReceiptData(null)
        }}
        data={receiptData}
        loading={receiptLoading}
      />
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {})(Statements)
